/* ============================================================
   CartTerms – No-bypass clickwrap gate for cart drawer & cart page
   Depends on: robin-functions.js (Robin global, optional)
   ============================================================ */
window.CartTerms = (function () {
  const LOCKED_CLASSES = ['opacity-50', 'cursor-not-allowed', 'pointer-events-none'];
  const SHAKE_CLASS = 'animate-shake';
  const ATTR_SYNC_EVENT = 'cart:terms:changed';

  let _initialized = false;

  function getCheckboxes() {
    return Array.from(document.querySelectorAll('[data-cart-terms-input]'));
  }

  function getButtonForPrefix(prefix) {
    return document.querySelector('[data-cart-terms-target="' + prefix + '"]');
  }

  function getRoot(checkbox) {
    return checkbox.closest('[data-cart-terms]');
  }

  function getErrorEl(root) {
    return root ? root.querySelector('[data-cart-terms-error]') : null;
  }

  function setButtonLocked(button, locked) {
    if (!button) return;
    if (locked) {
      button.setAttribute('aria-disabled', 'true');
      LOCKED_CLASSES.forEach(function (cls) {
        button.classList.add(cls);
      });
      if (button.tagName === 'BUTTON') {
        button.disabled = true;
      }
    } else {
      button.setAttribute('aria-disabled', 'false');
      LOCKED_CLASSES.forEach(function (cls) {
        button.classList.remove(cls);
      });
      if (button.tagName === 'BUTTON') {
        button.disabled = false;
      }
    }
  }

  function hideError(root) {
    const err = getErrorEl(root);
    if (err) err.classList.add('hidden');
  }

  function showError(root) {
    const err = getErrorEl(root);
    if (err) err.classList.remove('hidden');
  }

  function shake(el) {
    if (!el) return;
    el.classList.add(SHAKE_CLASS);
    setTimeout(function () {
      el.classList.remove(SHAKE_CLASS);
    }, 400);
  }

  function buildAttributes(root, checked) {
    if (!checked) {
      return {
        terms_accepted: '',
        terms_accepted_at: '',
        terms_min_age: '',
        terms_tos_url: '',
        terms_privacy_url: '',
      };
    }
    return {
      terms_accepted: 'true',
      terms_accepted_at: new Date().toISOString(),
      terms_min_age: root.dataset.minAge || '',
      terms_tos_url: root.dataset.tosUrl || '',
      terms_privacy_url: root.dataset.privacyUrl || '',
    };
  }

  function persistAttributes(attributes, options) {
    const opts = options || {};
    const body = JSON.stringify({ attributes: attributes });
    const url = '/cart/update.js';

    if (opts.beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      try {
        const blob = new Blob([body], { type: 'application/json' });
        const ok = navigator.sendBeacon(url, blob);
        if (ok) return Promise.resolve();
      } catch (e) {
        // fall through to fetch
      }
    }

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body,
      keepalive: !!opts.keepalive,
    }).catch(function () {
      // ignore – the gate's job is to block until checked, not to guarantee delivery
    });
  }

  function syncOtherCheckboxes(sourceCheckbox, checked) {
    getCheckboxes().forEach(function (cb) {
      if (cb === sourceCheckbox) return;
      if (cb.checked !== checked) {
        cb.checked = checked;
        const root = getRoot(cb);
        const prefix = root ? root.dataset.idPrefix : null;
        if (prefix) {
          setButtonLocked(getButtonForPrefix(prefix), !checked);
        }
        if (checked) hideError(root);
      }
    });
  }

  function handleChange(e) {
    const checkbox = e.currentTarget;
    const root = getRoot(checkbox);
    const prefix = root ? root.dataset.idPrefix : null;
    if (!root || !prefix) return;

    const button = getButtonForPrefix(prefix);
    setButtonLocked(button, !checkbox.checked);

    if (checkbox.checked) {
      hideError(root);
    }

    persistAttributes(buildAttributes(root, checkbox.checked));

    document.dispatchEvent(
      new CustomEvent(ATTR_SYNC_EVENT, {
        detail: { checked: checkbox.checked, prefix: prefix },
      }),
    );

    syncOtherCheckboxes(checkbox, checkbox.checked);
  }

  function findCheckboxForButton(button) {
    const prefix = button.dataset.cartTermsTarget;
    if (!prefix) return null;
    return document.getElementById('cart-terms-' + prefix);
  }

  function blockIfUnchecked(button, e) {
    const checkbox = findCheckboxForButton(button);
    if (!checkbox) return false;
    if (checkbox.checked) {
      // Refresh timestamp right before navigating / submitting so the
      // recorded consent reflects the actual moment of checkout.
      const root = getRoot(checkbox);
      persistAttributes(buildAttributes(root, true), { keepalive: true, beacon: true });
      return false;
    }
    e.preventDefault();
    e.stopPropagation();
    const root = getRoot(checkbox);
    showError(root);
    shake(root);
    try {
      checkbox.focus({ preventScroll: false });
    } catch (err) {
      checkbox.focus();
    }
    if (root && root.scrollIntoView) {
      root.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    return true;
  }

  function handleButtonClick(e) {
    const button = e.currentTarget;
    blockIfUnchecked(button, e);
  }

  function handleFormSubmit(e) {
    const form = e.currentTarget;
    const button = form.querySelector('[data-cart-terms-target]');
    if (!button) return;
    blockIfUnchecked(button, e);
  }

  function bindButton(button) {
    if (!button || button.dataset.cartTermsBound === 'true') return;
    button.dataset.cartTermsBound = 'true';
    button.addEventListener('click', handleButtonClick);

    if (button.tagName === 'BUTTON' && (button.type === 'submit' || button.getAttribute('form'))) {
      const formId = button.getAttribute('form');
      const form = formId ? document.getElementById(formId) : button.closest('form');
      if (form && form.dataset.cartTermsBound !== 'true') {
        form.dataset.cartTermsBound = 'true';
        form.addEventListener('submit', handleFormSubmit);
      }
    }
  }

  function bindCheckbox(checkbox) {
    if (!checkbox || checkbox.dataset.cartTermsBound === 'true') return;
    checkbox.dataset.cartTermsBound = 'true';
    checkbox.addEventListener('change', handleChange);
  }

  function init() {
    const checkboxes = getCheckboxes();
    if (!checkboxes.length) return;

    checkboxes.forEach(function (cb) {
      const root = getRoot(cb);
      const prefix = root ? root.dataset.idPrefix : null;
      if (!prefix) return;
      bindCheckbox(cb);
      const button = getButtonForPrefix(prefix);
      bindButton(button);
      // Always start unchecked + locked (clickwrap requires fresh affirmation).
      cb.checked = false;
      hideError(root);
      setButtonLocked(button, true);
    });

    // Reset stored consent on init so a stale "accepted" attribute can never
    // outlive the rendered (unchecked) UI state.
    persistAttributes(buildAttributes(document.createElement('div'), false));
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    init();
    _initialized = true;

    // Re-bind whenever the cart drawer (or any cart-related section) updates.
    window.addEventListener('cartUpdate', function () {
      init();
    });

    // Theme editor section reload (cart page).
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('[data-cart-terms]')) {
        init();
      }
    });
  });

  return {
    init: init,
    isInitialized: function () {
      return _initialized;
    },
  };
})();
